import moment from 'moment'

import { unwrapData, unwrapList } from './api'
import { getPatientRegisterRequiredFieldErrors } from './requireConfig'

export const ADDRESS_TYPES = [
  { key: 'citizen', apiKey: 'CITIZEN', label: 'ที่อยู่ตามบัตรประชาชน' },
  { key: 'current', apiKey: 'CURRENT', label: 'ที่อยู่ปัจจุบัน' },
  { key: 'origin', apiKey: 'ORIGIN', label: 'ที่อยู่ตามภูมิลำเนา' },
  { key: 'workplace', apiKey: 'WORKPLACE', label: 'ที่อยู่ที่ทำงาน' }
]

export const emptyAddress = () => ({
  personAddressId: null,
  houseNumber: '',
  villageCode: '',
  roadName: '',
  subDistrictCode: '',
  districtCode: '',
  provinceCode: '',
  postalCode: '',
  countryCode: 'THA',
  phoneNumber: '',
  isPrimary: 0,
  isEditable: 1,
  isInCups: 0,
  isRegiment: 0,
  isInBenefitRegion: 0,
  isSameCitizenAddress: 0
})

export const createEmptyForm = (defaultPrimaryBenefit = {}) => ({
  hn: '',
  person: {
    personId: null,
    citizenId: '',
    foreignCitizenId: '',
    passportNumber: '',
    prefixName: '',
    firstName: '',
    middleName: '',
    lastName: '',
    prefixNameEn: '',
    firstNameEn: '',
    middleNameEn: '',
    lastNameEn: '',
    aliasName: '',
    genderId: '',
    birthDate: '',
    birthTime: '00:00',
    cardExpiredDate: '',
    maritalStatusId: '',
    occupationCode: '',
    communicationLanguageCode: 'ไทย',
    citizenshipCode: '',
    nationalityCode: '',
    religionCode: '',
    phoneNumber: '',
    secondPhoneNumber: '',
    email: '',
    sexualOrientation: '',
    servicePreference: '',
    birthText: '',
    isFakePerson: 0,
    isFakeName: 0,
    isFakeBirthday: 0,
    isFakeCitizenId: 0
  },
  patient: {
    nmdHn: '',
    militaryNumber: '',
    privateSoldierId: '',
    navyPatientTypeId: '',
    navyPatientGroupId: '',
    bloodGroupCode: '',
    bloodGroupRh: '',
    referralChannelId: '',
    patientClassId: '',
    isUnknownAdr: 1,
    isNoAdr: 0,
    isAdr: 0,
    drugAllergy: '',
    drugAllergicReaction: '',
    concealedName: '',
    concealedNote: '',
    physicalDescription: '',
    isAnonymousPerson: 0,
    isHospitalOfficer: 0,
    isDeceased: 0,
    isDocumentDestroyed: 0,
    isAssumedHn: 0
  },
  addresses: ADDRESS_TYPES.reduce((result, item) => ({ ...result, [item.key]: emptyAddress() }), {}),
  contacts: [],
  primaryBenefit: {
    patientBenefitTypeId: null,
    seq: '1',
    benefitTypeCode: '10',
    benefitNumber: '',
    startDate: '',
    expiredDate: '',
    hospitalMainCode: '',
    hospitalSubCode: '',
    benefitPriceCategoryId: '',
    sponsorId: '',
    sponsorTypeId: '',
    policyNo: '',
    policyHolderName: '',
    relationshipToHolderId: '',
    coverageType: '',
    benefitLimit: '',
    perVisitLimit: '',
    roomLimitPerDay: '',
    approvalBy: '',
    note: '',
    ...defaultPrimaryBenefit,
    patientBenefitTypeId: null,
    seq: defaultPrimaryBenefit.seq || '1',
    isPrimary: 1
  },
  secondaryBenefits: [],
  regiment: {
    mainRegiment: '',
    subRegiment: '',
    employerTypeId: '',
    employerName: '',
    employerCitizenId: '',
    registrationAgency: '',
    governmentChronicNumber: '',
    governmentOfficerNumber: ''
  },
  familyHistory: [],
  deceased: {
    deceasedDate: '',
    deceasedTime: '',
    deceasedNumber: '',
    deceasedReasonCode: '',
    deceasedReasonText: '',
    deathCertificateDate: '',
    deathCertificateNumber: '',
    isPregnant: 0,
    weekPregnant: '',
    isAutopsyPerform: 0
  },
  notes: [],
  allergies: [],
  patientNetwork: [],
  chronicDiseases: [],
  nameChanges: [],
  hnChanges: []
})

const getResult = (results, name) => results.find((item) => item.name === name)?.response

const firstData = (response) => {
  const data = unwrapData(response)
  if (Array.isArray(data)) return data[0] || null
  if (Array.isArray(data?.data)) return data.data[0] || null
  if (Array.isArray(data?.items)) return data.items[0] || null
  return data || null
}

const asList = (response) => unwrapList(response).filter(Boolean)

const normalizeBenefitDate = (value) => {
  if (!value) return ''
  const text = String(value)
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text

  const date = moment.parseZone(text)
  return date.isValid() ? date.utcOffset(7).format('YYYY-MM-DD') : ''
}

const normalizeBenefit = (benefit = {}) => ({
  ...benefit,
  startDate: normalizeBenefitDate(benefit.startDate),
  expiredDate: normalizeBenefitDate(benefit.expiredDate)
})

export const normalizeRegistration = (results, hn, defaultPrimaryBenefit = {}) => {
  const benefits = asList(getResult(results, 'benefits')).map(normalizeBenefit)
  const empty = createEmptyForm(benefits.length ? {} : {
    ...defaultPrimaryBenefit,
    benefitTypeCode: defaultPrimaryBenefit.benefitTypeCode || '10'
  })
  const patient = firstData(getResult(results, 'patient')) || {}
  const person = firstData(getResult(results, 'person')) || patient.person || {}
  const primaryBenefit = benefits.find((benefit) => Number(benefit.isPrimary) === 1) || benefits[0] || null

  const addresses = ADDRESS_TYPES.reduce((result, item) => {
    const address = firstData(getResult(results, `address.${item.apiKey}`)) || {}
    return {
      ...result,
      [item.key]: { ...emptyAddress(), ...address }
    }
  }, {})

  return {
    ...empty,
    hn,
    person: { ...empty.person, ...person },
    patient: { ...empty.patient, ...patient, person: undefined },
    addresses,
    contacts: asList(getResult(results, 'contacts')),
    primaryBenefit: primaryBenefit ? { ...empty.primaryBenefit, ...primaryBenefit } : empty.primaryBenefit,
    secondaryBenefits: benefits.filter((benefit) => benefit !== primaryBenefit),
    regiment: {
      ...empty.regiment,
      ...(firstData(getResult(results, 'regiment')) || {})
    },
    familyHistory: asList(getResult(results, 'familyHistory')),
    deceased: {
      ...empty.deceased,
      ...(firstData(getResult(results, 'deceased')) || {})
    },
    notes: asList(getResult(results, 'notes')),
    allergies: asList(getResult(results, 'allergies')),
    patientNetwork: asList(getResult(results, 'patientNetwork')),
    chronicDiseases: asList(getResult(results, 'chronicDiseases')),
    nameChanges: asList(getResult(results, 'nameChanges')),
    hnChanges: asList(getResult(results, 'hnChanges'))
  }
}

export const sanitizeCitizenId = (value) => String(value || '').replace(/\D/g, '')

export const isValidCitizenId = (value) => {
  const citizenId = sanitizeCitizenId(value)
  if (citizenId.length !== 13 || /^([0-9])\1+$/.test(citizenId)) return false

  const sum = citizenId
    .slice(0, 12)
    .split('')
    .reduce((total, digit, index) => total + Number(digit) * (13 - index), 0)
  const checkDigit = (11 - (sum % 11)) % 10
  return checkDigit === Number(citizenId[12])
}

export const calculateAge = (birthDate) => {
  if (!birthDate || !moment(birthDate, 'YYYY-MM-DD', true).isValid()) return null
  const birth = moment(birthDate, 'YYYY-MM-DD')
  const today = moment()
  if (birth.isAfter(today)) return null

  const years = today.diff(birth, 'years')
  birth.add(years, 'years')
  const months = today.diff(birth, 'months')
  birth.add(months, 'months')
  const days = today.diff(birth, 'days')
  return { years, months, days }
}

export const validateRegistration = (form, requireConfig = {}) => {
  const errors = getPatientRegisterRequiredFieldErrors({ form, ...requireConfig })
  const warnings = []
  const person = form.person
  const citizenId = sanitizeCitizenId(person.citizenId)

  if (citizenId && !person.isFakeCitizenId && !isValidCitizenId(citizenId)) {
    errors.push('เลขบัตรประชนไม่ถูกต้อง')
  }
  if (person.birthDate && moment(person.birthDate).isAfter(moment(), 'day')) {
    errors.push('วันเกิดต้องไม่มากกว่าวันปัจจุบัน')
  }
  if (Boolean(Number(form.patient.isDeceased)) && !form.deceased.deceasedDate) {
    errors.push('กรุณาระบุวันที่เสียชีวิต')
  }
  if (person.cardExpiredDate && moment(person.cardExpiredDate).isSameOrBefore(moment(), 'day')) {
    warnings.push('บัตรประชาชนหมดอายุแล้ว')
  }

  const age = calculateAge(person.birthDate)
  const prefix = String(person.prefixName || '')
  if (age && age.years >= 15 && ['ด.ช.', 'ด.ญ.'].includes(prefix)) {
    warnings.push('คำนำหน้าชื่อไม่สัมพันธ์กับอายุ 15 ปีขึ้นไป')
  }

  return { errors, warnings }
}

const getFirstValue = (record, keys) => {
  for (const key of keys) {
    if (record?.[key] !== undefined && record?.[key] !== null) return record[key]
  }
  return ''
}

export const toOptions = (records, valueKeys, labelKeys) =>
  (records || [])
    .map((record) => {
      const value = getFirstValue(record, valueKeys)
      const label = getFirstValue(record, labelKeys)
      if (value === '' && label === '') return null
      return { value, label: label || String(value), record }
    })
    .filter(Boolean)

const PERSON_SMART_CARD_FIELDS = [
  'citizenId',
  'prefixName',
  'firstName',
  'middleName',
  'lastName',
  'prefixNameEn',
  'firstNameEn',
  'middleNameEn',
  'lastNameEn',
  'genderId',
  'religionCode',
  'birthDate',
  'cardExpiredDate'
]

const hasValue = (value) => value !== undefined && value !== null && value !== '' && value !== '-' && value !== 'Invalid Date'

const normalizeSmartCardDate = (value) => {
  if (!hasValue(value)) return ''
  const text = String(value).trim()
  let year
  let month
  let day

  if (/^\d{8}$/.test(text)) {
    year = Number(text.slice(0, 4))
    month = Number(text.slice(4, 6))
    day = Number(text.slice(6, 8))
  } else if (/^\d{4}-\d{2}-\d{2}/.test(text)) {
    const parts = text.slice(0, 10).split('-').map(Number)
    year = parts[0]
    month = parts[1]
    day = parts[2]
  } else {
    const date = moment(text)
    return date.isValid() ? date.format('YYYY-MM-DD') : ''
  }

  if (year > 2400) year -= 543
  const date = moment({ year, month: month - 1, day })
  return date.isValid() ? date.format('YYYY-MM-DD') : ''
}

const normalizeSmartCardAddress = (value) => {
  if (Array.isArray(value)) {
    return value.find((address) => address?.addressTypeCode === 'CITIZEN') || value[0] || {}
  }
  return value || {}
}

export const applySmartCardData = (form, scanData = {}) => {
  const profile = scanData.profile || scanData.cardProfile || scanData.smartCardData?.profile || {}
  const citizenAddress = normalizeSmartCardAddress(scanData.citizenAddress || scanData.cardCitizenAddress || scanData.smartCardData?.citizenAddress)
  const personData = PERSON_SMART_CARD_FIELDS.reduce((result, key) => {
    if (!hasValue(profile[key])) return result
    const value = ['birthDate', 'cardExpiredDate'].includes(key) ? normalizeSmartCardDate(profile[key]) : profile[key]
    return hasValue(value) ? { ...result, [key]: value } : result
  }, {})
  const addressData = Object.keys(emptyAddress()).reduce((result, key) => {
    const aliases = key === 'houseNumber' ? ['houseNumber', 'houseNo'] : [key]
    const value = aliases.map((alias) => citizenAddress[alias]).find(hasValue)
    return hasValue(value) ? { ...result, [key]: value } : result
  }, {})
  const addressNameData = [
    ['subDistrictName', ['subDistrictName', 'subdistrictName', 'subDistrict']],
    ['districtName', ['districtName', 'district']],
    ['provinceName', ['provinceName', 'province']],
    ['countryName', ['countryName', 'country']]
  ].reduce((result, [key, aliases]) => {
    const value = aliases.map((alias) => citizenAddress[alias]).find(hasValue)
    return hasValue(value) ? { ...result, [key]: value } : result
  }, {})

  return {
    ...form,
    person: {
      ...form.person,
      ...personData,
      citizenId: sanitizeCitizenId(personData.citizenId || form.person.citizenId),
      isFakePerson: 0,
      isFakeName: 0,
      isFakeBirthday: 0,
      isFakeCitizenId: 0
    },
    patient: { ...form.patient, isAnonymousPerson: 0 },
    addresses: {
      ...form.addresses,
      citizen: { ...form.addresses.citizen, ...addressData, ...addressNameData }
    }
  }
}

export const applyUnknownPatient = (form, fakeCitizenId, anonymous = {}) => {
  const genderId = anonymous.genderId ?? ''
  return {
    ...form,
    person: {
      ...form.person,
      citizenId: sanitizeCitizenId(fakeCitizenId),
      prefixName: anonymous.prefixName || '',
      firstName: anonymous.firstName || '',
      lastName: anonymous.lastName || '',
      genderId,
      birthDate: anonymous.birthDate || '',
      birthTime: '00:00',
      isFakePerson: 1,
      isFakeName: 1,
      isFakeBirthday: 1,
      isFakeCitizenId: 1
    },
    patient: {
      ...form.patient,
      physicalDescription: anonymous.physicalDescription || '',
      isAnonymousPerson: 1
    }
  }
}
